import { useState } from "react";
import { Breadcrumb } from "@/components/feature/Pagination";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6cq1fqhq9pk5mecsig";

const VALUES = [
  { icon: "ri-stack-line", title: "丰富题材", desc: "热血、恋爱、奇幻、悬疑、科幻等十余种题材一站浏览。" },
  { icon: "ri-time-line", title: "及时更新", desc: "热门连载章节持续更新，精彩内容不掉线。" },
  { icon: "ri-eye-line", title: "舒适阅读", desc: "字号与背景自由调节，白天夜晚都护眼。" },
  { icon: "ri-shield-check-line", title: "轻量安全", desc: "无需登录即可浏览，收藏仅保存在本地浏览器。" },
];

export default function About() {
  useDocumentTitle(
    "关于我们 - 老司机漫画",
    "了解老司机漫画的平台定位、内容特色与使用规范，并通过反馈表单与我们联系。",
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // 蜜罐校验：命中则静默当作成功，不发送任何请求
    const lure = String(formData.get("website_alt") || "").trim();
    if (lure) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const spam = String(serverMsg || "").toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !spam;

      if (ok) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && !spam
            ? String(serverMsg)
            : "提交失败，请检查填写内容后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <main className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />

      <header className="mt-4 mb-8 max-w-3xl">
        <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
          关于老司机漫画
        </h1>
        <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">
          老司机漫画是一个专注于热门漫画连载与精彩章节推荐的中文阅读平台。我们希望用清晰的分类、及时的更新与舒适的阅读体验，帮每一位漫画爱好者更快找到想看的故事。
        </p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {VALUES.map((v) => (
          <div key={v.title} className="p-5 rounded-lg bg-background-50 border border-background-200">
            <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
              <i className={`${v.icon} text-xl`}></i>
            </span>
            <h2 className="mt-3 font-heading font-bold text-base text-foreground-950">{v.title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">{v.desc}</p>
          </div>
        ))}
      </section>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
        <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200">
          <h2 className="font-heading font-bold text-xl text-foreground-950">联系我们</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-700">
            如果你对内容、功能有建议，或发现任何问题，欢迎通过下面的表单或邮箱与我们联系。我们会认真阅读每一条反馈。
          </p>
          <div className="mt-5 flex flex-col gap-3">
            <div className="flex items-center gap-3 text-sm">
              <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center shrink-0">
                <i className="ri-mail-line"></i>
              </span>
              <span className="text-foreground-800">feedback@laosiji-comic.demo</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="w-9 h-9 rounded-md bg-secondary-100 text-secondary-800 flex items-center justify-center shrink-0">
                <i className="ri-global-line"></i>
              </span>
              <span className="text-foreground-800">在线浏览 · 无需注册</span>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-lg bg-accent-50 border border-accent-100">
            <h3 className="font-heading font-bold text-sm text-accent-900">使用与版权规范</h3>
            <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-accent-900/80">
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                本站漫画、章节、封面与评分为演示示例数据，仅用于界面展示。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                站内收藏为浏览器本地存储，清除浏览器数据后会丢失。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                内容仅供学习交流，禁止用于商业用途或未经授权的转载。
              </li>
            </ul>
          </div>
        </div>

        <div className="p-6 md:p-8 rounded-xl bg-background-50 border border-background-200">
          <h2 className="font-heading font-bold text-xl text-foreground-950">发送反馈</h2>
          <p className="mt-2 text-sm text-foreground-500">留下你的想法，帮助我们做得更好。</p>

          {status === "success" ? (
            <div className="mt-6 p-6 rounded-lg bg-primary-50 border border-primary-200 text-center">
              <span className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center mx-auto">
                <i className="ri-check-line text-2xl"></i>
              </span>
              <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">提交成功</h3>
              <p className="mt-2 text-sm text-foreground-600">
                感谢你的反馈，我们已经收到啦。
              </p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-5 h-10 px-5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                再写一条
              </button>
            </div>
          ) : (
            <form
              id="about-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-5 flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    你的称呼
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入称呼"
                    className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="fb-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  反馈类型
                </label>
                <select
                  id="fb-subject"
                  name="subject"
                  defaultValue="内容建议"
                  className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-400"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="功能反馈">功能反馈</option>
                  <option value="问题报错">问题报错</option>
                  <option value="合作洽谈">合作洽谈</option>
                </select>
              </div>

              <div>
                <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  具体内容
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的建议或问题（500 字以内）"
                  className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm resize-none focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-field-lure"
              />

              {status === "error" && (
                <p className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                  <i className="ri-error-warning-line mr-1"></i>
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className={`h-12 rounded-md font-medium text-sm transition-colors whitespace-nowrap ${
                  status === "submitting"
                    ? "bg-primary-300 text-background-50 cursor-not-allowed"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer"
                }`}
              >
                {status === "submitting" ? "提交中…" : "提交反馈"}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="mt-10 p-6 md:p-8 rounded-xl bg-secondary-900 text-background-100">
        <h2 className="font-heading font-bold text-xl text-background-50">版权声明</h2>
        <p className="mt-3 text-sm leading-relaxed text-background-300 max-w-3xl">
          © 2026 老司机漫画。本站为演示站点，站内所有漫画作品、章节目录、封面图片、评分与收藏数量均为虚拟示例数据，仅用于界面与交互演示，不代表任何真实作品、作者或数据成就。请勿将本站内容用于任何商业用途；如需转载或引用，请先与我们取得联系并获得授权。
        </p>
      </section>
    </main>
  );
}